import { z } from "zod";

/**
 * M011/S01 T05: era-web boundary parsers for the journey progress data layer.
 *
 * `getJourneyProgressResponseSchema` mirrors the era-api response contract from
 * ../../era-api/src/schemas/journeyProgress.ts. The shape is intentionally
 * duplicated rather than imported across the repo boundary — the contract check
 * script (scripts/check-journey-progress-contract.ts) round-trips the era-api
 * fixture through this parser to catch drift at CI/dev time without coupling
 * the era-web bundle to era-api source.
 *
 * `journeyS3FileSchema` is the CDN journey JSON envelope used by the
 * useJourneyProgress hook (T06) to validate fetched content before merging it
 * with the progress payload. Existing journeyService.ts validates with
 * hand-rolled guards; this zod parser is the structural source of truth for
 * the M011 hook layer.
 */

const cdnIdSchema = z.string().min(1).max(50);

const journeyTypeSchema = z.string().min(1).max(50);

export const journeyProgressStatusEnum = [
	"not_started",
	"in_progress",
	"complete",
] as const;

export const journeyProgressStatusSchema = z.enum(journeyProgressStatusEnum);

export const stepStateSchema = z
	.object({
		stepId: cdnIdSchema,
		status: journeyProgressStatusSchema,
		openedAt: z.string().datetime().nullable(),
		closedAt: z.string().datetime().nullable(),
	})
	.strict();

export const taskProgressSchema = z
	.object({
		taskId: cdnIdSchema,
		status: journeyProgressStatusSchema,
		progressPercent: z.number().int().min(0).max(100).nullable(),
		completedAt: z.string().datetime().nullable(),
	})
	.strict();

export const getJourneyProgressResponseSchema = z
	.object({
		journeyType: journeyTypeSchema,
		startedAt: z.string().datetime().nullable(),
		stepStates: z.array(stepStateSchema),
		taskProgress: z.array(taskProgressSchema),
		completedTaskIds: z.array(cdnIdSchema),
	})
	.strict();

const journeyS3JourneyShape = z
	.object({
		id: z.string().min(1),
		type: journeyTypeSchema,
		name: z.string(),
		treatmentType: z.string(),
		totalSteps: z.number().int().nonnegative(),
		steps: z.array(z.unknown()),
	})
	.passthrough();

export const journeyS3FileSchema = z
	.object({
		schemaVersion: z.union([z.literal("1.0"), z.literal("2.0")]),
		locale: z.string().min(1),
		journeyType: journeyTypeSchema,
		generatedAt: z.string().min(1),
		journey: journeyS3JourneyShape,
	})
	.passthrough();

export type JourneyProgressStatus = z.infer<typeof journeyProgressStatusSchema>;
export type StepStatePayload = z.infer<typeof stepStateSchema>;
export type TaskProgressPayload = z.infer<typeof taskProgressSchema>;
export type GetJourneyProgressResponse = z.infer<
	typeof getJourneyProgressResponseSchema
>;
export type JourneyS3FilePayload = z.infer<typeof journeyS3FileSchema>;
